import { parseError } from "@features/shared/data";
import { createAtom } from "@taager/atom";
import { trans } from "@taager/localization";
import Loader from "design-system/components/Loader";
import { ExternalToast, toast } from "sonner";

export type ToastAtom = {
  type: "success" | "error" | "info";
  message: string;
  title?: string;
};

export const toastAtom = createAtom<ToastAtom>({
  key: "toast",
  default: {},
});

/**
 * Show a success toast message
 */
export function toastSuccess(message: string): void {
  toast(message, {
    duration: 5000,
    className:
      "bg-toast-primary-success text-white rounded-md p-4 flex items-center",
  });
}

/**
 * Show an error toast message
 */
export function toastError(message: string): void {
  toast(message, {
    duration: 5000,
    className:
      "bg-toast-primary-error text-white rounded-md p-4 flex items-center",
  });
}

/**
 * Show an info toast message
 */
export function toastInfo(message: string): void {
  toast(message, {
    duration: 5000,
    className:
      "bg-toast-primary-info text-white rounded-md p-4 flex items-center",
  });
}

/**
 * Show a warning toast message
 */
export function toastWarning(message: string): void {
  toast(message, {
    duration: 5000,
    className:
      "bg-toast-primary-danger text-white rounded-md p-4 flex items-center",
  });
}

export function toastLoader(
  message: string = trans("loading"),
  options?: ExternalToast,
) {
  const id = toast.loading(
    <div className="flex items-center gap-2 p-3">
      <Loader size="xs" />
      <span>{message}</span>
    </div>,
    options,
  );

  const dismiss = (callback?: () => void) => {
    toast.dismiss(id);
    callback?.();
  };

  return {
    success: (message: string) => dismiss(() => toastSuccess(message)),
    error: (message: string) => dismiss(() => toastError(message)),
    warning: (message: string) => dismiss(() => toastWarning(message)),
    info: (message: string) => dismiss(() => toastInfo(message)),
    dismiss,
  };
}

/**
 * Parse and show error message in toast
 */
export function catchError(error: any): void {
  toastError(parseError(error));
}
